import classNames from 'classnames'
import React from 'react'

import { Body2, Title2 } from '@ui/Typography/Typography'
import { Button, IconButton } from '@ui/components/Button'
import { ButtonVariant } from '@ui/components/Button/Button'
import { RichIcon } from '@ui/components/RichIcon'
import { TextColorType } from '@ui/design-system/colors/colors'
import { CloseIcon } from '@ui/icons/Close.icon'
import { InfoFilledIcon } from '@ui/icons/InfoFilled.icon'
import { IconRenderer, IconRendererType } from '@ui/icons/utils/IconRenderer'

import { Box } from '@shared/components/Box'
import { PageNoticeType } from '@shared/components/PageNotice/PageNoticeContext'

import styles from './AlertNotice.module.scss'

export interface AlertNoticeProps {
  id: string
  title?: string
  message?: string | React.ReactNode
  type: PageNoticeType
  isPageNotice?: boolean
  richIcon?: boolean
  icon?: IconRendererType
  noIcon?: boolean
  action?: {
    label: string
    onClick: () => void
  }
  secondaryAction?: {
    label: string
    onClick: () => void
  }
  onClose?: () => void
}

export const AlertNotice: React.FC<AlertNoticeProps> = (note) => {
  const handleAction = () => note.action?.onClick()
  const handleSecondaryAction = () => note.secondaryAction?.onClick()

  const iconColor: Record<PageNoticeType, TextColorType> = {
    neutral: 'text-med-em',
    danger: 'text-danger-med-em',
    success: 'text-success-med-em',
    warning: 'text-warning-med-em',
    info: 'text-info-med-em',
    dark: 'text-inverse-black-neutral',
  }

  const buttonVariant: Record<PageNoticeType, ButtonVariant> = {
    neutral: 'primary',
    danger: 'danger',
    success: 'success',
    warning: 'warning',
    info: 'info',
    dark: 'primary',
  }

  const secondaryColor: TextColorType =
    note.type === 'dark' ? 'text-inverse-white-neutral' : 'text-inverse-black-neutral'

  const icon: IconRendererType = !note.icon
    ? { icon: InfoFilledIcon, color: iconColor[note.type], secondaryColor }
    : typeof note.icon === 'function'
    ? {
        icon: note.icon,
        color: note.type === 'dark' ? 'text-med-em' : iconColor[note.type],
        secondaryColor,
      }
    : note.icon

  return (
    <div
      className={classNames(styles.note, styles[note.type], {
        [styles.isPageNotice]: note.isPageNotice,
        [styles.noTitle]: !note.title,
      })}
    >
      <div className={styles.innerWrapper}>
        <div className={styles.content}>
          {note.title && (
            <div className={styles.titleRow}>
              {!note.noIcon &&
                (note.richIcon ? (
                  <RichIcon
                    shape={'squircle'}
                    bg={'surface-surface-1'}
                    border={'outline-low-em'}
                    icon={icon}
                    size='x24'
                  />
                ) : (
                  <IconRenderer icon={icon} size={24} />
                ))}

              <Title2
                medium
                color={note.type === 'dark' ? 'text-inverse-black-neutral' : 'text-high-em'}
              >
                {note.title}
              </Title2>
            </div>
          )}
          {note.message && (
            <Body2 color={note.type === 'dark' ? 'text-inverse-black-neutral' : 'text-med-em'}>
              {note.message}
            </Body2>
          )}
        </div>
        {(note.action || note.secondaryAction) && (
          <Box align='center' gap='x8'>
            {note.action && (
              <Button variant={buttonVariant[note.type]} onClick={handleAction}>
                {note.action.label}
              </Button>
            )}
            {note.secondaryAction && (
              <Button.Ghost onClick={handleSecondaryAction}>
                {note.secondaryAction.label}
              </Button.Ghost>
            )}
          </Box>
        )}
      </div>

      {note.onClose && <IconButton.Ghost size='sm' icon={CloseIcon} onClick={note.onClose} />}
    </div>
  )
}
